import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { css } from '../../../../styled-system/css'
import { ValidatableTextInput } from '../../../components/validatable-inputs/text-input'
import { ValidatableSelect } from '../../../components/validatable-inputs/select'
import { LoadingOverlay } from '../../../components/loading-overlay'
import { useCreateOperationFundSource } from '../../../libs/admin/hooks'
import { button } from '../../../styles/buttonStyles'
import { contentWrapper } from '../../../styles/contentWrapper'
import { inputLabel, inputWrapper, radio, requiredFieldLabel, validationMessageLabel } from '../../../styles/inputStyles'
import { pageTitle, sectionWrapper, titleWrapper } from '../../../styles/sectionStyles'
import { OperationFundSource_Type } from 'schema/gen/es/chiikipoint/model/v2/model_pb'
import { newFundSourceSchema } from './-schemas/new-fund-source'

export const Route = createFileRoute('/admin/fundSources/newFundSource')({
  component: RouteComponent,
})

const fundSourceTypeOptions = [
  { value: OperationFundSource_Type.DISTRIBUTABLE, label: '配布ポイント' },
  { value: OperationFundSource_Type.STANDALONE_DISTRIBUTABLE, label: '配布ポイント（外部）' },
  { value: OperationFundSource_Type.ADMIN_GRANT, label: '管理コンソール発行ポイント' },
  { value: OperationFundSource_Type.PS_INTERNAL, label: 'ミニアプリ連携ポイント' },
]

function RouteComponent() {
  const navigate = useNavigate()
  const createOperationFundSource = useCreateOperationFundSource()
  const [fundSourceName, setFundSourceName] = useState('')
  const [fundSourceType, setFundSourceType] = useState<OperationFundSource_Type>(
    fundSourceTypeOptions[0]?.value ?? OperationFundSource_Type.UNSPECIFIED
  )
  const [isTransferable, setIsTransferable] = useState(true)

  const parseResult = useMemo(() => {
    return newFundSourceSchema.safeParse({
      name: fundSourceName,
      type: fundSourceType,
      isTransferable,
    })
  }, [fundSourceName, fundSourceType, isTransferable])
  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}
  const nameError = fieldErrors.name?.[0]
  const typeError = fieldErrors.type?.[0]
  const transferableError = fieldErrors.isTransferable?.[0]

  const handleCreateFundSource = async () => {
    if (!parseResult.success) {
      return
    }
    try {
      const data = {
        type: parseResult.data.type,
        defaultGrantReason: parseResult.data.name,
        forbidTransfer: !parseResult.data.isTransferable,
      }
      await createOperationFundSource.mutateAsync(data)
      navigate({ to: '/admin/fundSources' })
    } catch (error) {
      console.error('Failed to create fund source:', error)
    }
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>新規ポイント作成</h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      })}>
        <div className={sectionWrapper()}>
          <ValidatableTextInput
            title="ポイント名"
            placeholder="ポイント名を入力してください"
            value={fundSourceName}
            onChange={(value) => setFundSourceName(value)}
            type="text"
            isRequired
            isValid={!nameError}
            validationMessage={nameError ?? 'OK'}
          />

          <ValidatableSelect
            title="ポイントタイプ"
            value={String(fundSourceType)}
            onChange={(value) => setFundSourceType(Number(value) as OperationFundSource_Type)}
            options={fundSourceTypeOptions.map((option) => ({
              label: option.label,
              value: String(option.value),
            }))}
            isRequired
            isValid={!typeError}
            validationMessage={typeError ?? 'OK'}
          />

          <div className={inputWrapper()}>
            <div className={inputLabel()}>
              ポイントの譲渡
              <span className={requiredFieldLabel()}>必須</span>
            </div>
            <div className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            })}>
            <label className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            })}>
              <input
                type="radio"
                name="transferable"
                checked={isTransferable}
                onChange={() => {
                  setIsTransferable(true)
                }}
                className={radio()}
              />
              <span className={css({ fontSize: '14px', color: 'text.primary' })}>可</span>
            </label>
            <label className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            })}>
              <input
                type="radio"
                name="transferable"
                checked={!isTransferable}
                onChange={() => {
                  setIsTransferable(false)
                }}
                className={radio()}
              />
              <span className={css({ fontSize: '14px', color: 'text.primary' })}>不可</span>
            </label>
          </div>
          <div className={validationMessageLabel({ semantic: transferableError ? 'invalid' : undefined })}>
            {transferableError ? transferableError : '\u00a0'}
          </div>
        </div>
        </div>
        <div className={css({
          display: 'flex',
        })}>
          <button
            type="button"
            className={button({ color: 'primary' })}
            onClick={handleCreateFundSource}
            disabled={createOperationFundSource.isPending || !parseResult.success}
          >
            新規ポイント発行
            {createOperationFundSource.isPending && <LoadingOverlay size="sm" />}
          </button>
        </div>
      </div>
    </div>
  )
}
